<template>
  <form class="ds-container" @submit.prevent="save">
    <!-- Basic Information -->
    <div class="form-card">
      <div class="form-card__header">
        <div class="form-card__header-icon"><i class="pi pi-info-circle"></i></div>
        <div>
          <p class="form-card__title">البيانات الأساسية</p>
          <p class="form-card__subtitle">اسم السنة الدراسية والوصف والمدرّس المسؤول عنها</p>
        </div>
      </div>
      <div class="form-card__body">
        <div class="ds-field-grid">
          <div class="form-field ds-field--full">
            <label class="field-label">اسم السنة الدراسية <span class="required">*</span></label>
            <input type="text" class="form-control" v-model="form.name" placeholder="مثال: الصف الأول الثانوي" />
          </div>
          <div class="form-field">
            <label class="field-label">اسم المعلم <span class="optional">اختياري</span></label>
            <input type="text" class="form-control" v-model="form.teacher" placeholder="اسم المدرّس" />
          </div>
          <div class="form-field">
            <label class="field-label">اللون الأساسي <span class="optional">اختياري</span></label>
            <input type="color" class="form-control form-control-color w-100" v-model="colorValue" />
          </div>
          <div class="form-field ds-field--full">
            <label class="field-label">الوصف <span class="optional">اختياري</span></label>
            <textarea class="form-control" rows="3" v-model="form.description" placeholder="وصف مختصر يظهر للطالب..."></textarea>
          </div>
          <div class="form-field">
            <label class="field-label">تاريخ بداية احتساب الغياب <span class="optional">اختياري</span></label>
            <input type="date" class="form-control" v-model="form.attendance_start_date" />
            <small class="text-muted d-block mt-1">التواريخ قبل هذا التاريخ لا تُحتسب ضمن جدول أو غياب هذه السنة الدراسية فقط.</small>
          </div>
        </div>
      </div>
    </div>

    <ProductImagesSection :preview="preview" @file-change="onImage" />

    <ProductPricingSection :form="form" />

    <ProductSettingsSection
      :form="form"
      subtitle="حالة السنة الدراسية وظهورها واشتراكها"
      visible-desc="تظهر السنة في صفحة السنوات الدراسية العامة"
      home-desc="تظهر ضمن السنوات المميزة بالصفحة الرئيسية"
    >
      <template #before>
        <label class="settings-row">
          <div class="settings-row__text">
            <span class="settings-row__title">مفعّلة</span>
            <span class="settings-row__desc">السنة الدراسية متاحة للاستخدام في المنصة</span>
          </div>
          <InputSwitch v-model="form.is_active" />
        </label>
      </template>
    </ProductSettingsSection>

    <div class="form-actions form-actions--sticky">
      <button type="submit" class="btn-submit" :disabled="saving || !form.name.trim()">
        <i class="pi pi-spin pi-spinner-dotted" v-if="saving"></i>
        <i class="pi pi-check" v-else></i>
        {{ saving ? 'جارٍ الحفظ...' : 'حفظ السنة الدراسية' }}
      </button>
      <button type="button" class="btn-cancel" @click="$router.push('/academic-years')">إلغاء</button>
    </div>
  </form>
</template>

<script>
import axios from 'axios';
import InputSwitch from 'primevue/inputswitch';
import Swal from 'sweetalert2';
import ProductImagesSection from '@/views/shared/ProductImagesSection.vue';
import ProductPricingSection from '@/views/shared/ProductPricingSection.vue';
import ProductSettingsSection from '@/views/shared/ProductSettingsSection.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';
import { apiError } from '@/composables/subscriptionMeta.js';

function emptyForm() {
  return {
    name: '', description: '', teacher: '', color: '',
    is_active: true, is_visible: true, is_subscription_enabled: false, show_on_home: false,
    monthly_price: '', term1_price: '', term2_price: '', yearly_price: '',
    term1_end_date: '', term2_end_date: '',
    // Per-academic-year attendance calculation boundary — NOT a global
    // setting; each academic year keeps its own value.
    attendance_start_date: '',
  };
}

export default {
  name: 'AcademicYearForm',
  components: { InputSwitch, ProductImagesSection, ProductPricingSection, ProductSettingsSection },
  props: {
    year: { type: Object, default: null },
  },
  data() {
    return {
      form: emptyForm(),
      files: { image: null, cover_image: null },
      preview: { image: null, cover_image: null },
      saving: false,
    };
  },
  computed: {
    // <input type="color"> requires a well-formed #rrggbb value at all times.
    colorValue: {
      get() { return this.form.color || '#5B5FEF'; },
      set(v) { this.form.color = v; },
    },
  },
  watch: {
    year: {
      immediate: true,
      handler(y) {
        if (!y) return;
        this.form = {
          name: y.name ?? '', description: y.description ?? '', teacher: y.teacher ?? '', color: y.color ?? '',
          is_active: !!y.is_active,
          is_visible: y.is_visible === undefined ? true : !!y.is_visible,
          is_subscription_enabled: !!y.is_subscription_enabled,
          show_on_home: !!y.show_on_home,
          monthly_price: y.monthly_price ?? '', term1_price: y.term1_price ?? '',
          term2_price: y.term2_price ?? '', yearly_price: y.yearly_price ?? '',
          term1_end_date: y.term1_end_date ? String(y.term1_end_date).slice(0, 10) : '',
          term2_end_date: y.term2_end_date ? String(y.term2_end_date).slice(0, 10) : '',
          // This academic year's OWN value — never a shared/global default.
          attendance_start_date: y.attendance_start_date ? String(y.attendance_start_date).slice(0, 10) : '',
        };
        this.preview = { image: y.image_link || null, cover_image: y.cover_image_link || null };
      },
    },
  },
  methods: {
    onImage(field, file) {
      this.files[field] = file;
      this.preview[field] = URL.createObjectURL(file);
    },
    buildFormData() {
      const fd = new FormData();
      fd.append('_method', 'PUT'); // Laravel method spoofing so multipart (image) parses.
      const f = this.form;
      fd.append('name', f.name.trim());
      fd.append('description', f.description ?? '');
      fd.append('teacher', f.teacher ?? '');
      fd.append('color', f.color ?? '');
      fd.append('is_active', f.is_active ? 1 : 0);
      fd.append('is_visible', f.is_visible ? 1 : 0);
      fd.append('is_subscription_enabled', f.is_subscription_enabled ? 1 : 0);
      fd.append('show_on_home', f.show_on_home ? 1 : 0);
      ['monthly_price', 'term1_price', 'term2_price', 'yearly_price'].forEach(k =>
        fd.append(k, f[k] === '' ? '' : f[k]));
      fd.append('term1_end_date', f.term1_end_date ?? '');
      fd.append('term2_end_date', f.term2_end_date ?? '');
      fd.append('attendance_start_date', f.attendance_start_date ?? '');
      if (this.files.image) fd.append('image', this.files.image);
      if (this.files.cover_image) fd.append('cover_image', this.files.cover_image);
      return fd;
    },
    async save() {
      this.saving = true;
      try {
        const fd = this.buildFormData();
        const { data } = await axios.post(`/academic-years/${this.year.id}`, fd);
        const gradeStore = useGradeStore();
        const idx = gradeStore.allGrades.findIndex(g => g.id === this.year.id);
        if (idx !== -1) gradeStore.allGrades[idx] = { ...gradeStore.allGrades[idx], ...data.data };
        await Swal.fire({ icon: 'success', title: 'تم الحفظ', timer: 1200, showConfirmButton: false });
        this.$router.push('/academic-years');
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
